import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OrgStatus } from '@construction/shared';
import { orgsApi, type Org } from '@/api/endpoints';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  Input,
  ListGroup,
  ListRow,
  PageHeader,
  RowActions,
  Spinner,
} from '@/components/ui';
import { useOrgs } from '@/features/orgs';
import { useErrorMessage } from '@/hooks/useErrorMessage';

/**
 * 单位（CR-073）：多租户部署时叫「公司」，单独部署给一家大公司时叫「分公司」（文案由 ORG_KIND 换）。
 *
 * 只有超管进得来（路由上 `system`）：开一家、改名、归档是租户的边界，和「谁能发权限」一样不能是一个勾。
 * **只归档不删除**：删了挂在上面的人就成了看得到全部的平台账号。归档之后不能再往里建人、建工地、建角色，
 * 已有的账号照常登录 —— 要停掉一家的登录，在用户页按这家筛出来停用。
 *
 * CR-082：表格 + 行内「改名 / 归档」（改之前一家一张卡片）。窄屏、App 上是列表，点一行在弹出层里改名、归档。
 */
export function OrgsPage() {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const orgs = useOrgs();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const editing = orgs.all.find((org) => org.id === editingId) ?? null;

  return (
    <>
      <PageHeader
        title={t('org.title')}
        description={t('org.description')}
        action={
          <Button onClick={() => setCreating(true)} data-action="org.create">
            {t('org.create')}
          </Button>
        }
      />

      <Drawer open={creating} title={t('org.create')} onClose={() => setCreating(false)}>
        <OrgForm onDone={() => setCreating(false)} />
      </Drawer>
      <Drawer
        open={editing !== null}
        title={editing ? t('org.renameTitle', { name: editing.name }) : ''}
        subtitle={editing && <OrgCounts org={editing} />}
        onClose={() => setEditingId(null)}
      >
        {editing && (
          <div className="flex flex-col gap-6">
            <OrgForm key={editing.id} org={editing} onDone={() => setEditingId(null)} />
            {/* 窄屏上行内没有按钮，归档也在这里 */}
            <div className="border-t border-rule pt-5 xl:hidden">
              <ArchiveSection org={editing} />
            </div>
          </div>
        )}
      </Drawer>

      {orgs.isLoading && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}
      {orgs.isError && <Alert>{toMessage(orgs.error)}</Alert>}
      {orgs.data && orgs.all.length === 0 && <EmptyState title={t('org.empty')} />}

      {orgs.all.length > 0 && (
        <>
          <ListGroup className="xl:hidden">
            {orgs.all.map((org) => (
              <ListRow
                key={org.id}
                onClick={() => setEditingId(org.id)}
                title={org.name}
                subtitle={<OrgCounts org={org} />}
                trailing={<StatusBadge org={org} />}
              />
            ))}
          </ListGroup>

          <DataTable
            minWidth="48rem"
            columns={[
              { key: 'name', label: t('org.name') },
              { key: 'sites', label: t('org.sitesColumn'), align: 'right' as const, className: 'w-24' },
              { key: 'users', label: t('org.usersColumn'), align: 'right' as const, className: 'w-24' },
              { key: 'status', label: t('org.statusColumn'), className: 'w-28' },
              { key: 'created', label: t('org.createdColumn'), className: 'w-32' },
              { key: 'actions', className: 'w-44' },
            ]}
          >
            {orgs.all.map((org) => (
              <OrgRow key={org.id} org={org} onRename={() => setEditingId(org.id)} />
            ))}
          </DataTable>
        </>
      )}
    </>
  );
}

function OrgCounts({ org }: { org: Org }) {
  const { t } = useTranslation();
  return (
    <span className="tabular-nums">
      {t('org.siteCount', { count: org._count.sites })} · {t('org.userCount', { count: org._count.users })}
    </span>
  );
}

function StatusBadge({ org }: { org: Org }) {
  const { t } = useTranslation();
  return org.status === OrgStatus.ARCHIVED ? (
    <Badge tone="neutral">{t('org.archived')}</Badge>
  ) : (
    <Badge tone="ok">{t('org.active')}</Badge>
  );
}

function OrgRow({ org, onRename }: { org: Org; onRename: () => void }) {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  return (
    <tr>
      <td>
        <span className="block truncate font-medium text-ink">{org.name}</span>
        {error && <span className="block text-caption text-danger">{error}</span>}
      </td>
      <td className="whitespace-nowrap text-right tabular-nums text-ink-2">{org._count.sites}</td>
      <td className="whitespace-nowrap text-right tabular-nums text-ink-2">{org._count.users}</td>
      <td className="whitespace-nowrap">
        <StatusBadge org={org} />
      </td>
      <td className="whitespace-nowrap text-caption tabular-nums text-ink-3">
        {new Date(org.createdAt).toLocaleDateString(i18n.language)}
      </td>
      <td>
        <RowActions>
          <Button size="sm" variant="ghost" onClick={onRename}>
            {t('org.rename')}
          </Button>
          <ArchiveButton org={org} size="sm" onError={setError} />
        </RowActions>
      </td>
    </tr>
  );
}

/** 归档要二次确认（和工地归档一样，CR-072）；恢复不用 */
function ArchiveButton({
  org,
  size,
  onError,
}: {
  org: Org;
  size?: 'sm';
  onError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const archived = org.status === OrgStatus.ARCHIVED;

  const toggle = useMutation({
    mutationFn: () => orgsApi.update(org.id, { status: archived ? OrgStatus.ACTIVE : OrgStatus.ARCHIVED }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orgs'] }),
    onError: (err) => onError(toMessage(err)),
  });

  return (
    <Button
      size={size}
      variant={archived || size === 'sm' ? 'ghost' : 'dangerOutline'}
      loading={toggle.isPending}
      onClick={() => {
        onError(null);
        if (archived || window.confirm(t('org.archiveConfirm', { name: org.name }))) toggle.mutate();
      }}
    >
      {archived ? t('org.restore') : t('org.archive')}
    </Button>
  );
}

/** 弹出层里的归档（窄屏、App 上行内没有按钮）：错误显示在按钮上面 */
function ArchiveSection({ org }: { org: Org }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="flex flex-col items-start gap-3">
      {error && <Alert>{error}</Alert>}
      <ArchiveButton org={org} onError={setError} />
    </div>
  );
}

function OrgForm({ org, onDone }: { org?: Org; onDone: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const [name, setName] = useState(org?.name ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => (org ? orgsApi.update(org.id, { name: name.trim() }) : orgsApi.create(name.trim())),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orgs'] });
      // 用户、工地、角色列表里带着单位名，改名后跟着刷
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      onDone();
    },
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
      className="flex flex-col gap-5"
    >
      {error && <Alert>{error}</Alert>}
      <Field label={t('org.name')} htmlFor="org-name" required hint={t('org.nameHint')}>
        <Input id="org-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={64} />
      </Field>
      {!org && <Alert tone="info">{t('org.createNext')}</Alert>}
      <div className="flex gap-3">
        <Button type="submit" loading={save.isPending}>
          {org ? t('app.save') : t('org.create')}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t('app.cancel')}
        </Button>
      </div>
    </form>
  );
}
